import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/products/${slug}`)
      .then(res => {
        setProduct(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="inline-block w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Product Not Found</h1>
          <Link to="/products" className="text-primary">Back to Products</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Link to="/products" className="inline-flex items-center text-primary mb-8">
          <ArrowLeft className="mr-2" size={20} />
          <span className="font-mono text-sm uppercase">BACK TO PRODUCTS</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="glass p-8 flex items-center justify-center aspect-square">
            <div className="text-center">
              <div className="w-32 h-32 bg-primary/10 border-2 border-primary/30 flex items-center justify-center mx-auto mb-4">
                <span className="text-primary text-6xl font-bold">{product.name.charAt(0)}</span>
              </div>
            </div>
          </div>

          <div>
            <span className="inline-block px-4 py-1 border border-primary/30 text-primary text-xs font-mono uppercase mb-4">
              {product.category}
            </span>
            <h1 className="text-4xl font-bold mb-4">{product.name}</h1>
            <p className="text-lg text-muted-foreground mb-6">{product.description}</p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="glass p-6">
                <div className="text-xs font-mono text-muted-foreground uppercase mb-2">Energy Savings</div>
                <div className="text-3xl font-bold text-primary">{product.energy_savings}</div>
              </div>
              <div className="glass p-6">
                <div className="text-xs font-mono text-muted-foreground uppercase mb-2">Efficiency</div>
                <div className="text-3xl font-bold text-primary">{product.efficiency}</div>
              </div>
            </div>

            {product.features && product.features.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 uppercase">Key Features</h2>
                <div className="space-y-3">
                  {product.features.slice(0, 10).map((feat, idx) => {
                    return (
                      <div key={`feat-${idx}`} className="flex items-start">
                        <Check className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{String(feat)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {product.specs && Object.keys(product.specs).length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 uppercase">Specifications</h2>
                <div className="glass p-4 space-y-2">
                  {Object.entries(product.specs).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                      <span className="text-muted-foreground text-sm font-mono">{key.replace(/_/g, ' ')}</span>
                      <span className="text-primary font-semibold">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-4">
              <Link to="/contact" className="flex-1 px-8 py-4 bg-primary text-black font-mono uppercase text-sm font-bold text-center">
                REQUEST A QUOTE
              </Link>
              <a href="tel:+17787292202" className="flex-1 px-8 py-4 border border-primary/50 text-primary font-mono uppercase text-sm text-center">
                <Phone size={18} className="inline mr-2" />
                CALL
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
